import { useClipboard } from "@mantine/hooks";
import cx from "classnames";
import { useCallback } from "react";
import { t } from "ttag";

import Styles from "metabase/css/core/index.css";
import { Icon, Text, Tooltip } from "metabase/ui";

import CopyButtonStyles from "./CopyButton.module.css";

export const COPY_BUTTON_ICON = (
  <Icon
    className={CopyButtonStyles.icon}
    name="copy"
    aria-hidden
    aria-label={undefined}
  />
);

type CopyButtonProps = {
  value: string;
  onCopy?: () => void;
  className?: string;
  style?: object;
  "aria-label"?: string;
  target?: React.ReactNode;
};

export const CopyButton = ({
  value,
  onCopy,
  className = cx(Styles.textBrandHover, Styles.cursorPointer),
  style,
  "aria-label": ariaLabel,
  target = COPY_BUTTON_ICON,
}: CopyButtonProps) => {
  const clipboard = useClipboard({ timeout: 2000 });

  const onCopyValue = useCallback(() => {
    clipboard.copy(value);
    onCopy?.();
  }, [clipboard, value, onCopy]);

  // Icon-only default needs an explicit name. Custom targets that include
  // visible text (e.g. EntityCopyButton) should be named from that content
  // so the accessible name matches what users see.
  const resolvedAriaLabel =
    ariaLabel ?? (target === COPY_BUTTON_ICON ? t`Copy` : undefined);

  return (
    <>
      <button
        type="button"
        className={cx(CopyButtonStyles.CopyButton, className)}
        data-testid="copy-button"
        onClick={onCopyValue}
        style={style}
        aria-label={resolvedAriaLabel}
      >
        <Tooltip
          label={<Text fw={700} c="inherit">{t`Copied!`}</Text>}
          opened={clipboard.copied}
        >
          <span>{target}</span>
        </Tooltip>
      </button>
      {/* Tooltip is visual-only; Mantine doesn't expose it to assistive tech.
          Keep a polite live region as a sibling of the button so screen readers
          hear "Copied!" (WCAG 4.1.3) without nesting role=status inside the
          button (WCAG 4.1.2 / aria_descendant_valid). */}
      <span
        role="status"
        aria-live="polite"
        className={CopyButtonStyles.srOnly}
      >
        {clipboard.copied ? t`Copied!` : ""}
      </span>
    </>
  );
};
